import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Car, Clock, MapPin, MessageSquare, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MapView } from "@/components/MapView";
import { api } from "@/lib/api";
import { STATUS_LABELS } from "@/lib/constants";

const PHASE_TEXT = {
  pending: "Waiting for confirmation", to_pickup: "Your driver is on the way to pickup",
  on_trip: "You're on your way to your destination", completed: "Journey completed",
};

export default function TrackRide() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const timer = useRef(null);

  const load = () => api.get(`/bookings/${id}/track`).then(({ data }) => setT(data)).catch(() => {});
  useEffect(() => { load(); timer.current = setInterval(load, 4000); return () => clearInterval(timer.current); /* eslint-disable-next-line */ }, [id]);

  if (!t) return <div className="flex h-96 items-center justify-center"><Loader2 className="animate-spin text-brand" size={32} /></div>;

  const driverOffers = t.driver_pos ? [{ offer_id: "driver", lat: t.driver_pos.lat, lng: t.driver_pos.lng }] : [];

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4 gap-1"><ArrowLeft size={16} /> Back</Button>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <MapView pickup={t.pickup} dropoff={t.dropoff} offers={driverOffers} />
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-light text-brand"><Navigation size={18} /></span>
              <div>
                <p className="text-xs text-slate-400">{t.booking_ref}</p>
                <p className="font-heading font-semibold text-navy">{STATUS_LABELS[t.status] || t.status}</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-600">{PHASE_TEXT[t.phase]}</p>
            {t.eta_min != null && t.phase !== "completed" && (
              <div className="mt-3 flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-3">
                <Clock size={16} className="text-brand" />
                <span className="text-sm text-slate-700">{t.phase === "to_pickup" ? "Arriving in" : "Arriving at destination in"} <span className="font-semibold text-navy">~{t.eta_min} min</span></span>
              </div>
            )}
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-brand transition-all duration-700" style={{ width: `${Math.round(t.frac * 100)}%` }} />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="flex items-center gap-2 text-sm font-medium text-navy"><Car size={16} className="text-brand" /> {t.vehicle_label}</p>
            <p className="mt-1 text-sm text-slate-500">{t.driver_name || "Awaiting driver"} · {t.operator_name}</p>
            <div className="mt-3 space-y-1.5 text-sm">
              <p className="flex items-center gap-2 text-slate-600"><MapPin size={14} className="text-emerald-500" /> {t.pickup?.name}</p>
              {t.dropoff && <p className="flex items-center gap-2 text-slate-600"><MapPin size={14} className="text-brand" /> {t.dropoff.name}</p>}
            </div>
            <Button variant="outline" className="mt-4 w-full gap-2" onClick={() => navigate("/messages")} data-testid="track-message-driver"><MessageSquare size={15} /> Message Driver</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
